// Care Plan module config — types, groupings, review cycles, status helpers.
// Wave 1 upgrade: person-centred terminology, grouped Fluent-native dropdown,
// review-status colour badges, auto-review-date defaults.

// Grouped Care Plan Types — renders as native <optgroup>s in RecordDialog.
export const CARE_PLAN_TYPE_GROUPS = [
  {
    label: "Assessment",
    options: [
      "Initial Assessment",
      "Needs Assessment",
    ],
  },
  {
    label: "Core Care Plans",
    options: [
      "Personal Support Plan",
      "Personal Care Plan",
      "Behaviour Support Plan",
      "Positive Behaviour Support (PBS)",
      "Medication Support Plan",
      "Communication Plan",
      "Nutrition & Hydration Plan",
      "Mobility & Moving & Handling Plan",
      "Emotional Wellbeing Plan",
      "Sleep & Night Support Plan",
      "Community Access Plan",
    ],
  },
  {
    label: "Clinical & Specialist",
    options: [
      "Diabetes Management Plan",
      "Epilepsy Care Plan",
      "Dysphagia (Eating & Drinking) Plan",
      "Pressure Care / Skin Integrity Plan",
      "Continence Care Plan",
      "Mental Health Support Plan",
      "End of Life Care Plan",
    ],
  },
  {
    label: "Legal & Safeguarding",
    options: [
      "Mental Capacity Assessment (MCA)",
      "DoLS / Liberty Protection Safeguards (LPS)",
      "Best Interest Decision",
      "Consent Plan",
    ],
  },
  {
    label: "Other",
    options: [
      "Positive Risk Taking Plan",
      "Other",
    ],
  },
];

export const ALL_CARE_PLAN_TYPES = CARE_PLAN_TYPE_GROUPS.flatMap((g) => g.options);

// Review cycles (months) — defaults suggested by CQC best practice.
// Manager can override the date on the form; this is only the initial default.
export const REVIEW_MONTHS_BY_TYPE = {
  "Initial Assessment": 6,
  "Needs Assessment": 6,
  "Personal Support Plan": 12,
  "Personal Care Plan": 12,
  "Behaviour Support Plan": 6,
  "Positive Behaviour Support (PBS)": 6,
  "Medication Support Plan": 3,
  "Communication Plan": 12,
  "Nutrition & Hydration Plan": 6,
  "Mobility & Moving & Handling Plan": 6,
  "Emotional Wellbeing Plan": 6,
  "Sleep & Night Support Plan": 6,
  "Community Access Plan": 12,
  "Diabetes Management Plan": 6,
  "Epilepsy Care Plan": 6,
  "Dysphagia (Eating & Drinking) Plan": 6,
  "Pressure Care / Skin Integrity Plan": 3,
  "Continence Care Plan": 6,
  "Mental Health Support Plan": 6,
  "End of Life Care Plan": 1,
  "Mental Capacity Assessment (MCA)": 12,
  "DoLS / Liberty Protection Safeguards (LPS)": 12,
  "Best Interest Decision": 12,
  "Consent Plan": 12,
  "Positive Risk Taking Plan": 6,
  "Other": 12,
};

/** Add N months to an ISO date, returning YYYY-MM-DD. */
export const addMonthsISO = (months, fromDate = new Date()) => {
  const d = new Date(fromDate);
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
};

/** Compute review status ("current" | "due_soon" | "overdue" | "no_date") from a next_review_date string. */
export const computeReviewStatus = (nextReviewDate) => {
  if (!nextReviewDate) return "no_date";
  const now = new Date();
  const next = new Date(nextReviewDate);
  if (isNaN(next.getTime())) return "no_date";
  const days = Math.floor((next - now) / 86400000);
  if (days < 0) return "overdue";
  if (days <= 30) return "due_soon";
  return "current";
};

export const REVIEW_STATUS_STYLE = {
  overdue: {
    label: "Overdue",
    className: "bg-red-50 text-red-700 border-red-300 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800",
    dot: "bg-red-500",
  },
  due_soon: {
    label: "Review Due Soon",
    className: "bg-amber-50 text-amber-800 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800",
    dot: "bg-amber-500",
  },
  current: {
    label: "Current",
    className: "bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800",
    dot: "bg-emerald-500",
  },
  no_date: {
    label: "No Review Date",
    className: "bg-slate-50 text-slate-600 border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-600",
    dot: "bg-slate-400",
  },
};

export const PRIORITY_STYLE = {
  High: "bg-red-50 text-red-700 border-red-200",
  Medium: "bg-amber-50 text-amber-800 border-amber-200",
  Low: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export const PROGRESS_STYLE = {
  "On Track": "bg-sky-50 text-sky-700 border-sky-200",
  "Improving": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Needs Review": "bg-amber-50 text-amber-800 border-amber-200",
  "Goal Achieved": "bg-violet-50 text-violet-700 border-violet-200",
};

/**
 * Side-effect callback used by the form: when the user picks a Care Plan Type,
 * auto-populate `next_review_date` if it's empty. Manager can override.
 */
export const carePlanTypeSideEffect = (newType, currentValues) => {
  if (currentValues.next_review_date) return {}; // don't override manual choice
  const months = REVIEW_MONTHS_BY_TYPE[newType];
  if (!months) return {};
  return { next_review_date: addMonthsISO(months) };
};
